/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react'
import Lottie from 'lottie-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import chartLoading from './Animations/chartLoading.json'

export interface BarDataItem {
  time: string
  orders: number
}

export interface DispatchBarChartProps {
  barData: BarDataItem[]
  barChartIsLoading?: boolean
  showBarChart?: boolean
  barChartError?: string
}

export const DispatchBarChart: React.FC<DispatchBarChartProps> = ({
  barData,
  barChartIsLoading,
  showBarChart,
  barChartError,
}) => {
  const totalOrders = barData.reduce((sum, item) => sum + item.orders, 0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 420)
    }

    handleResize() // initial
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div className='bg-white rounded-xl p-4 sm:p-6 w-full'>
      <h2 className='text-N-600 text-center text-lg sm:text-xl font-semibold mb-4'>
        Order Dispatch Time Distribution
      </h2>
      {barChartIsLoading ? (
        <div className='bg-white w-full p-4 md:p-6 rounded-r-xl flex justify-center items-center'>
          <Lottie
            animationData={chartLoading}
            loop
            style={{ width: 300, height: 300 }}
          />
        </div>
      ) : (
        <div className='w-full min-w-[200px] h-[300px] sm:h-[400px] overflow-x-auto bg-N-100 rounded-xl'>
          {showBarChart && (
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={barData}
                margin={{ top: 20, right: 30, left: 20, bottom: 50 }} // extra bottom margin for rotated text
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis
                  dataKey='time'
                  interval={0}
                  angle={isMobile ? -45 : 0} // ✅ apply angle directly here
                  tick={{ fontSize: 10, fill: '#555', dy: 10 }}>
                  <Label
                    value='Dispatched Time'
                    offset={-40}
                    position='insideBottom'
                    style={{
                      fill: '#555',
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  />
                </XAxis>

                <YAxis>
                  <Label
                    value='Number of Orders'
                    angle={-90}
                    position='insideLeft'
                    style={{
                      textAnchor: 'middle',
                      fill: '#555',
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  />
                </YAxis>
                <Tooltip />
                <Bar dataKey='orders' fill='#f97316'>
                  <LabelList
                    dataKey='orders'
                    content={({ x, y, width, value, index }) => {
                      let percent = '0'
                      if (
                        typeof index === 'number' &&
                        index >= 0 &&
                        index < barData.length &&
                        totalOrders > 0
                      ) {
                        percent = (
                          (barData[index].orders / totalOrders) *
                          100.0
                        ).toFixed(2)
                      }
                      return (
                        <text
                          x={(Number(x) || 0) + (Number(width) || 0) / 2}
                          y={(typeof y === 'number' ? y : Number(y) || 0) - 10}
                          textAnchor='middle'
                          fill='#000'
                          fontSize={12}
                          fontWeight='bold'>
                          {percent}%
                        </text>
                      )
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {!showBarChart && barChartError && (
            <div className='bg-P-300 text-black font-semibold text-center px-4 py-3 rounded shadow'>
              {barChartError}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
